<script>
	import { onMount } from 'svelte';
	import { getApi } from '../../service/api';
	import { parseSleepStage, parseSamsungStages, parseHeartRate, parseLocalTimeMs } from './components/DataParser.js';
	import SleepStageChart from './components/SleepStageChart.svelte';
	import HeartRateChart from './components/HeartRateChart.svelte';
	import StageRatioChart from './components/StageRatioChart.svelte';
	import ComparisonCard from './components/ComparisonCard.svelte';
	import SessionInfoCard from './components/SessionInfoCard.svelte';

	// URL에서 id 추출 (tinro 방식)
	let id = '';
	$: {
		const path = window.location.pathname;
		const match = path.match(/\/samsung\/compare\/(\d+)/);
		if (match) id = match[1];
	}

	let sleepwaveData = null;
	let samsungDataList = [];
	let userEmail = '';
	let loading = true;

	// 파싱된 차트 데이터
	let ourStageData = [];
	let samsungStageData = [];
	let ourHeartRateData = [];
	let samsungHeartRateData = [];
	let ourRatios = null;
	let samsungRatios = null;

	async function fetchCompareData() {
		if (!id) return;
		loading = true;
		try {
			const res = await getApi(`/samsung/compare/${id}`);
			if (res && res.resultCode === 1000) {
				sleepwaveData = res.data?.sleepwave;
				samsungDataList = res.data?.samsung || [];
				userEmail = res.data?.userEmail || '';
				parseData();
			}
		} catch (e) {
			console.error('비교 데이터 로드 실패:', e);
		}
		loading = false;
	}

	function parseData() {
		// Sleepwave 수면 단계 (기존과 동일)
		if (sleepwaveData?.rawData?.rawSleepStage && sleepwaveData?.sleepData?.timeStart) {
			const startTime = parseLocalTimeMs(sleepwaveData.sleepData.timeStart);
			ourStageData = parseSleepStage(sleepwaveData.rawData.rawSleepStage, startTime);
		}

		// Samsung 수면 단계 — 여러 세션의 stages 병합
		const allSamsungStages = samsungDataList.flatMap(d => d.stages || []);
		allSamsungStages.sort((a, b) => a.startTime - b.startTime);
		samsungStageData = parseSamsungStages(allSamsungStages);

		// Sleepwave 심박수 (기존과 동일)
		if (sleepwaveData?.rawData?.rawHeartRate && sleepwaveData?.sleepData?.timeStart) {
			const startTime = parseLocalTimeMs(sleepwaveData.sleepData.timeStart);
			ourHeartRateData = parseHeartRate(sleepwaveData.rawData.rawHeartRate, startTime);
		}

		// Samsung 심박수 — 여러 세션의 heartRates 병합
		const allSamsungHR = samsungDataList.flatMap(d => d.heartRates || []);
		allSamsungHR.sort((a, b) => a.startTime - b.startTime);
		samsungHeartRateData = allSamsungHR;

		// Sleepwave 비율 (기존과 동일)
		if (sleepwaveData?.sleepData) {
			const sd = sleepwaveData.sleepData;
			ourRatios = {
				deep: sd.deepSleepRate || 0,
				light: sd.lightSleepRate || 0,
				rem: sd.remSleepRate || 0,
				awake: sd.wasoRate || 0,
			};
		}

		// Samsung 비율 — 여러 세션의 stages 합산 후 비율 계산
		if (allSamsungStages.length > 0) {
			const totals = { DEEP: 0, LIGHT: 0, REM: 0, AWAKE: 0 };
			for (const s of allSamsungStages) {
				const dur = (s.endTime - s.startTime) / 1000;
				if (totals[s.stage] !== undefined) totals[s.stage] += dur;
			}
			const totalDuration = samsungDataList.reduce((sum, d) => sum + (d.sleep?.duration || 0), 0);
			samsungRatios = {
				deep: totalDuration > 0 ? (totals.DEEP / totalDuration) * 100 : 0,
				light: totalDuration > 0 ? (totals.LIGHT / totalDuration) * 100 : 0,
				rem: totalDuration > 0 ? (totals.REM / totalDuration) * 100 : 0,
				awake: totalDuration > 0 ? (totals.AWAKE / totalDuration) * 100 : 0,
			};
		}
	}

	onMount(() => {
		fetchCompareData();
	});

	// 병합된 삼성 데이터 (ComparisonCard용)
	$: mergedSamsungData = samsungDataList.length > 0 ? {
		stages: samsungDataList.flatMap(d => d.stages || []),
		sleep: {
			duration: samsungDataList.reduce((sum, d) => sum + (d.sleep?.duration || 0), 0),
			startTime: Math.min(...samsungDataList.map(d => d.sleep?.startTime || Infinity)),
		}
	} : null;

	function formatDate(dateStr) {
		if (!dateStr) return '';
		return new Date(dateStr).toLocaleDateString('ko-KR');
	}
</script>

<div class="container-xxl flex-grow-1 container-p-y">
	<div class="d-flex align-items-center mb-4">
		<a href="/samsung/sessions" class="btn btn-sm btn-outline-secondary me-3">
			<i class="bx bx-arrow-back"></i>
		</a>
		<h4 class="fw-bold py-3 mb-0">
			수면 상세 비교
			{#if sleepwaveData?.sleepData}
				<span class="text-muted fs-6 ms-2">
					#{id} | {formatDate(sleepwaveData.sleepData.ymd)} | {userEmail || 'User'} (#{sleepwaveData.sleepData.idUser})
				</span>
			{/if}
		</h4>
	</div>

	{#if loading}
		<div class="text-center py-5">
			<div class="spinner-border" role="status"></div>
			<p class="mt-2 text-muted">데이터 로딩중...</p>
		</div>
	{:else if !sleepwaveData}
		<div class="alert alert-warning">데이터를 찾을 수 없습니다.</div>
	{:else}
		<!-- Samsung 데이터 유무 표시 -->
		{#if samsungDataList.length === 0}
			<div class="alert alert-info mb-4">
				<i class="bx bx-info-circle me-1"></i>
				이 세션에 매칭되는 Samsung Health 데이터가 없습니다. Sleepwave 데이터만 표시합니다.
			</div>
		{/if}

		<!-- 섹션 0: 세션 메타 정보 (헤더값) -->
		<SessionInfoCard sleepData={sleepwaveData?.sleepData} samsungCount={samsungDataList.length} />

		<!-- 섹션 A: 요약 카드 -->
		<ComparisonCard ourData={sleepwaveData} samsungData={mergedSamsungData} />

		<!-- 섹션 B: 수면 단계 차트 -->
		<SleepStageChart ourData={ourStageData} samsungData={samsungStageData} />

		<!-- 섹션 C: 심박수 차트 -->
		<HeartRateChart ourData={ourHeartRateData} samsungData={samsungHeartRateData} />

		<!-- 섹션 D: 수면 단계 비율 -->
		<StageRatioChart {ourRatios} {samsungRatios} />
	{/if}
</div>
